import type {
  Customer,
  Job,
  WhatsAppTemplateConfig,
  WhatsAppTwilioTemplate,
} from '@/lib/schemas/db'
import { toWhatsAppNumber } from '@/lib/whatsapp'

import type { NewWhatsAppMessageInput } from '../domain/models'

/** Substitute {{name}} placeholders in a template body with resolved values. */
export function renderTemplateBody(
  body: string,
  variables: Record<string, string>,
): string {
  return body.replace(
    /\{\{\s*([\w.]+)\s*\}\}/g,
    (_match, name: string) => variables[name] ?? '',
  )
}

/** The two job statuses that trigger a collection message, and their config key. */
export const collectionMessageKeyByStatus = {
  ReadyForCollection: 'ready_for_collection_successful',
  ReadyForCollectionUnsuccessful: 'ready_for_collection_unsuccessful',
} as const

export type CollectionMessageStatus = keyof typeof collectionMessageKeyByStatus

const summaryByStatus: Record<CollectionMessageStatus, string> = {
  ReadyForCollection: 'Your device is repaired and ready for collection.',
  ReadyForCollectionUnsuccessful:
    'Your device is ready for collection (repair unsuccessful).',
}

/**
 * Resolve a template field key (from whatsappTemplateFields) to a value, drawing
 * from the job's denormalized contact details first, then the linked customer.
 */
export function resolveTemplateField(
  key: string,
  ctx: { job: Job; customer?: Customer },
): string {
  const { job, customer } = ctx
  const fullName = job.contactName || customer?.fullName || ''
  switch (key) {
    case 'customer_full_name':
      return fullName
    case 'customer_first_name':
      return fullName.split(' ')[0] ?? ''
    case 'customer_phone':
      return job.contactPhone || customer?.contactPhone || ''
    case 'job_public_id':
      return job.publicId || ''
    case 'job_number_tag':
      return job.numberTag || ''
    case 'device_type':
      return job.deviceType || ''
    case 'job_fault':
      return job.fault || ''
    default:
      return ''
  }
}

/**
 * Build the WhatsApp message to queue when a job reaches a ready-for-collection
 * status. Uses the configured Twilio template (with variables resolved from the
 * job/customer) when one is set, otherwise a plain-text summary. Returns null
 * when there's no phone number to send to.
 */
export function composeReadyForCollectionMessage(deps: {
  job: Job
  customer?: Customer
  status: CollectionMessageStatus
  templateConfig?: WhatsAppTemplateConfig
  /** Cached Twilio template — its body lets us record the real rendered text. */
  twilioTemplate?: WhatsAppTwilioTemplate
}): NewWhatsAppMessageInput | null {
  const { job, customer, status, templateConfig, twilioTemplate } = deps

  const toPhone = toWhatsAppNumber(
    job.contactPhone || customer?.contactPhone || '',
  )
  if (!toPhone) return null

  const templateSid = templateConfig?.contentSid || undefined
  let templateVariables: Record<string, string> | undefined
  if (templateSid && templateConfig?.variables) {
    templateVariables = {}
    for (const [name, fieldKey] of Object.entries(templateConfig.variables)) {
      templateVariables[name] = resolveTemplateField(fieldKey, { job, customer })
    }
  }

  // Record the actual message: render the template body with the resolved
  // variables so the log shows what the customer receives, not a placeholder.
  // Falls back to a plain summary when there's no template body to render.
  const content =
    templateSid && twilioTemplate?.body && templateVariables
      ? renderTemplateBody(twilioTemplate.body, templateVariables)
      : summaryByStatus[status]

  return {
    toPhone,
    customerId: job.customerId || undefined,
    serviceId: job.id,
    servicePublicId: job.publicId,
    content,
    templateSid,
    templateVariables,
  }
}
